import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { QueryClient, useQuery, type QueryKey } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { useArtistAuth } from './ArtistAuthContext';

export function useBadgeSessionKey() {
  const [logoutGeneration, setLogoutGeneration] = useState(0);
  useEffect(() => {
    const clear = () => setLogoutGeneration((value) => value + 1);
    window.addEventListener('casticart:logout', clear);
    return () => window.removeEventListener('casticart:logout', clear);
  }, []);
  const { user } = useAuth() || {};
  const { artistUser } = useArtistAuth() || {};
  return JSON.stringify([logoutGeneration,
    user?.userId ?? user?.id, user?.userType, user?.role,
    user?.isLoggedIn, user?.is_external_account, user?.permissions, user?.permission_entries,
    artistUser?.userId ?? artistUser?.id, artistUser?.isLoggedIn,
  ]);
}

const BadgeResourceContext = createContext<{ client: QueryClient; scope: string } | null>(null);

// A separate, bounded badge cache uses the existing Query library. Its keys
// change synchronously at the authentication boundary, before children render.
export function BadgeResourceProvider({ children }: { children: React.ReactNode }) {
  const scope = useBadgeSessionKey();
  const client = useMemo(() => new QueryClient({ defaultOptions: { queries: {
    staleTime: 3000, gcTime: Infinity, retry: false,
    refetchOnWindowFocus: false, refetchOnReconnect: false,
  } } }), []);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    const onRestored = () => {
      if (document.visibilityState !== 'hidden') void client.invalidateQueries({ refetchType: 'active' });
    };
    window.addEventListener('casticart:connectivity-restored', onRestored);
    const unsubscribe = client.getQueryCache().subscribe((event) => {
      const inactive = client.getQueryCache().getAll().filter((query) => !query.getObserversCount() && query !== event.query);
      if (inactive.length <= 100) return;
      inactive.sort((a, b) => a.state.dataUpdatedAt - b.state.dataUpdatedAt);
      client.removeQueries({ queryKey: inactive[0].queryKey, exact: true });
    });
    return () => {
      unsubscribe();
      window.removeEventListener('casticart:connectivity-restored', onRestored);
      mounted.current = false;
      queueMicrotask(() => { if (!mounted.current) client.clear(); });
    };
  }, [client]);
  useEffect(() => () => {
    queueMicrotask(() => {
      if (currentScope.current !== scope) client.removeQueries({ queryKey: [scope] });
    });
  }, [client, scope]);
  const value = useMemo(() => ({ client, scope }), [client, scope]);
  return <BadgeResourceContext.Provider value={value}>{children}</BadgeResourceContext.Provider>;
}

export function useBadgeQuery<T>({
  queryKey, queryFn, enabled = true, visible = enabled, initialValue, refreshIntervalMs = 0,
}: {
  queryKey: QueryKey;
  queryFn: (context: { signal: AbortSignal }) => Promise<T>;
  enabled?: boolean;
  visible?: boolean;
  initialValue: T;
  refreshIntervalMs?: number;
}) {
  const context = useContext(BadgeResourceContext);
  if (!context) throw new Error('Badge queries require BadgeResourceProvider');
  const { client, scope } = context;
  const currentContext = useRef(context);
  currentContext.current = context;
  const query = useQuery({
    queryKey: [scope, ...queryKey], queryFn, enabled,
    refetchInterval: refreshIntervalMs > 0 ? Math.max(60000, refreshIntervalMs) : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: refreshIntervalMs > 0,
  }, client);
  const refresh = useCallback(async () => {
    if (!enabled || currentContext.current !== context) return initialValue;
    const result = await query.refetch();
    return result.data ?? initialValue;
  }, [context, enabled, initialValue, query.refetch]);
  return {
    data: visible ? query.data ?? initialValue : initialValue,
    loading: enabled && query.isFetching,
    refresh,
  };
}
